import React, { useState, useRef, useEffect } from "react";
import Swal from "sweetalert2";
import { Spinner } from "react-bootstrap";
import { $axios } from "../Helper/axios.config";
import Sidebar from "../components/Sidebar";

export default function AccountSettings() {
  // Loading state for initial data fetch
  const [loading, setLoading] = useState(true);

  // Email change state
  const [emailStep, setEmailStep] = useState("form"); // 'form' or 'verify'
  const [newEmail, setNewEmail] = useState("");
  const [emailOtpCode, setEmailOtpCode] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailResendCooldown, setEmailResendCooldown] = useState(0);

  // Phone change state
  const [phoneStep, setPhoneStep] = useState("form"); // 'form' or 'verify'
  const [newPhone, setNewPhone] = useState("");
  const [phoneOtpCode, setPhoneOtpCode] = useState("");
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneResendCooldown, setPhoneResendCooldown] = useState(0);

  // Refs for interval cleanup
  const emailIntervalRef = useRef(null);
  const phoneIntervalRef = useRef(null);

  // Fetch current user info on mount and pre-fill forms
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const userId = localStorage.getItem("userid");
        if (userId) {
          const res = await $axios.get(`/Registration/getRegistrationByUserID/${userId}`);
          const userInfo = res.data[0];
          const authEmail = localStorage.getItem("email");
          // Pre-fill email and phone fields with current values (support both camelCase and lowercase field names)
          setNewEmail(authEmail || userInfo?.contactEmail || userInfo?.contactemail || "");
          setNewPhone(userInfo?.contactTelephone || userInfo?.contacttelephone || localStorage.getItem("phone") || localStorage.getItem("contacttelephone") || "");
        } else {
          // Fallback to localStorage values
          setNewEmail(localStorage.getItem("email") || "");
          setNewPhone(localStorage.getItem("phone") || localStorage.getItem("contacttelephone") || "");
        }
      } catch (error) {
        console.error("Failed to fetch user info:", error);
        // Fallback to localStorage values
        setNewEmail(localStorage.getItem("email") || "");
        setNewPhone(localStorage.getItem("phone") || localStorage.getItem("contacttelephone") || "");
      } finally {
        setLoading(false);
      }
    };
    fetchUserInfo();
  }, []);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (emailIntervalRef.current) {
        clearInterval(emailIntervalRef.current);
      }
      if (phoneIntervalRef.current) {
        clearInterval(phoneIntervalRef.current);
      }
    };
  }, []);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  // Validation helpers
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim().toLowerCase());
  };

  const validatePhone = (phone) => {
    const cleanedPhone = phone.replace(/[\s-]/g, "");
    const phoneRegex = /^(\+?234|0)?[789][01]\d{8}$/;
    return phoneRegex.test(cleanedPhone);
  };

  const validatePassword = (password) => {
    if (!password || password.length < 6) return "Password must be at least 6 characters";
    if (!/[0-9]/.test(password)) return "Must contain at least one number";
    if (!/[a-z]/.test(password)) return "Must contain at least one lowercase letter";
    if (!/[A-Z]/.test(password)) return "Must contain at least one uppercase letter";
    if (!/[\W_]/.test(password)) return "Must contain at least one special character";
    return null;
  };

  // Password strength calculator
  const calculatePasswordStrength = (password) => {
    if (!password) return { score: 0, label: "", color: "", percentage: 0 };

    let score = 0;
    if (password.length >= 6) score += 1;
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[\W_]/.test(password)) score += 1;

    let label, color;
    if (score <= 2) {
      label = "Weak";
      color = "#dc3545";
    } else if (score <= 4) {
      label = "Fair";
      color = "#ffc107";
    } else if (score <= 6) {
      label = "Good";
      color = "#17a2b8";
    } else {
      label = "Strong";
      color = "#28a745";
    }

    return { score, label, color, percentage: (score / 7) * 100 };
  };

  // Email change handlers
  const handleEmailRequestOtp = async () => {
    if (!validateEmail(newEmail)) {
      Swal.fire({
        title: "Invalid Email",
        text: "Please enter a valid email address",
        icon: "error",
      });
      return;
    }

    setEmailLoading(true);
    try {
      await $axios.post("/v2/user/email/request-update", {
        newEmail: newEmail.trim().toLowerCase(),
      });

      setEmailStep("verify");
      setEmailResendCooldown(60);
      // Clear any existing interval before starting a new one
      if (emailIntervalRef.current) {
        clearInterval(emailIntervalRef.current);
      }
      emailIntervalRef.current = setInterval(() => {
        setEmailResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(emailIntervalRef.current);
            emailIntervalRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      Swal.fire({
        title: "Verification Code Sent",
        text: "A 6-digit verification code has been sent to your new email address.",
        icon: "success",
      });
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || "Failed to send verification code",
        icon: "error",
      });
    } finally {
      setEmailLoading(false);
    }
  };

  const handleEmailConfirmUpdate = async () => {
    if (emailOtpCode.length !== 6) {
      Swal.fire({
        title: "Invalid Code",
        text: "Please enter the 6-digit verification code",
        icon: "error",
      });
      return;
    }

    setEmailLoading(true);
    try {
      await $axios.post("/v2/user/email/confirm-update", {
        newEmail: newEmail.trim().toLowerCase(),
        otpCode: emailOtpCode,
      });

      Swal.fire({
        title: "Success",
        text: "Your email address has been updated successfully.",
        icon: "success",
      });

      // Update localStorage with new email
      localStorage.setItem("email", newEmail.trim().toLowerCase());

      // Reset to form view (keep the new email displayed in the field)
      setEmailStep("form");
      setNewEmail(newEmail.trim().toLowerCase());
      setEmailOtpCode("");
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || "Failed to verify code",
        icon: "error",
      });
    } finally {
      setEmailLoading(false);
    }
  };

  // Phone change handlers
  const handlePhoneRequestOtp = async () => {
    if (!validatePhone(newPhone)) {
      Swal.fire({
        title: "Invalid Phone Number",
        text: "Please enter a valid Nigerian phone number (e.g., 08012345678)",
        icon: "error",
      });
      return;
    }

    setPhoneLoading(true);
    try {
      await $axios.post("/v2/user/phone/request-update", {
        newPhone: newPhone,
      });

      setPhoneStep("verify");
      setPhoneResendCooldown(60);
      // Clear any existing interval before starting a new one
      if (phoneIntervalRef.current) {
        clearInterval(phoneIntervalRef.current);
      }
      phoneIntervalRef.current = setInterval(() => {
        setPhoneResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(phoneIntervalRef.current);
            phoneIntervalRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      Swal.fire({
        title: "Verification Code Sent",
        text: "A 6-digit verification code has been sent to your email address. Please verify to complete the phone number update.",
        icon: "success",
      });
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || "Failed to send verification code",
        icon: "error",
      });
    } finally {
      setPhoneLoading(false);
    }
  };

  const handlePhoneConfirmUpdate = async () => {
    if (phoneOtpCode.length !== 6) {
      Swal.fire({
        title: "Invalid Code",
        text: "Please enter the 6-digit verification code",
        icon: "error",
      });
      return;
    }

    setPhoneLoading(true);
    try {
      await $axios.post("/v2/user/phone/confirm-update", {
        newPhone: newPhone,
        otpCode: phoneOtpCode,
      });

      Swal.fire({
        title: "Success",
        text: "Your phone number has been updated successfully.",
        icon: "success",
      });

      // Update localStorage with new phone (use 'phone' key for consistency with other parts of the app)
      localStorage.setItem("phone", newPhone);
      localStorage.setItem("contacttelephone", newPhone);

      // Reset to form view (keep the new phone displayed in the field)
      setPhoneStep("form");
      setPhoneOtpCode("");
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || "Failed to verify code",
        icon: "error",
      });
    } finally {
      setPhoneLoading(false);
    }
  };

  // Password change handler
  const handlePasswordChange = async () => {
    if (!currentPassword) {
      Swal.fire({
        title: "Error",
        text: "Please enter your current password",
        icon: "error",
      });
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      Swal.fire({
        title: "Invalid Password",
        text: passwordError,
        icon: "error",
      });
      return;
    }

    if (newPassword !== confirmNewPassword) {
      Swal.fire({
        title: "Error",
        text: "New password and confirmation do not match",
        icon: "error",
      });
      return;
    }

    setPasswordLoading(true);
    try {
      await $axios.put("/v2/user/password/change", {
        currentPassword,
        newPassword,
        confirmNewPassword,
      });

      Swal.fire({
        title: "Success",
        text: "Your password has been changed successfully.",
        icon: "success",
      });

      // Reset form
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || "Failed to change password",
        icon: "error",
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  const passwordStrength = calculatePasswordStrength(newPassword);

  return (
    <div className="g-sidenav-show">
      <Sidebar />

      <main
        className="main-content position-relative max-height-vh-100 h-100 border-radius-lg"
        style={{ width: "80%", float: "right" }}
      >
        <div
          className="container-fluid justify-content-center py-4"
          style={{ width: "100%", position: "relative", left: 0 }}
        >
          <div className="container-fluid px-4">
            <div className="row">
              <div
                className="col-12"
                style={{ backgroundColor: "white", padding: 20 }}
              >
                <h4 style={{ color: "#003314", marginBottom: 30 }}>
                  Account Settings
                </h4>

                {loading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" /> Loading account information...
                  </div>
                ) : (
                  <>
                {/* Email Change Section */}
                <div className="row" style={{ padding: 15 }}>
                  <label className="mb-3 h5" style={{ color: "green" }}>
                    Email Address
                  </label>

                  {emailStep === "form" ? (
                    <>
                      <div className="col-sm-6 col-lg-4 col-md-6 mb-3">
                        <div className="input-group input-group-outline mb-3">
                          <input
                            className="form-control shadow-none"
                            type="email"
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            placeholder="Enter email address"
                          />
                        </div>
                      </div>
                      <div className="col-12">
                        <button
                          className="btn btn-success"
                          onClick={handleEmailRequestOtp}
                          disabled={emailLoading || !newEmail}
                          type="button"
                        >
                          {emailLoading ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            "Send Verification Code"
                          )}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-muted">
                        A verification code has been sent to: <strong>{newEmail}</strong>
                      </p>
                      <div className="col-sm-6 col-lg-4 col-md-6 mb-3">
                        <label className="form-label text-dark">
                          Enter 6-digit code
                        </label>
                        <div className="input-group input-group-outline mb-3">
                          <input
                            className="form-control shadow-none text-center"
                            type="text"
                            maxLength="6"
                            value={emailOtpCode}
                            onChange={(e) =>
                              setEmailOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                            }
                            style={{ fontSize: 18, letterSpacing: 8 }}
                          />
                        </div>
                      </div>
                      <div className="col-12">
                        <button
                          className="btn btn-success me-2"
                          onClick={handleEmailConfirmUpdate}
                          disabled={emailLoading || emailOtpCode.length !== 6}
                          type="button"
                        >
                          {emailLoading ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            "Verify & Update"
                          )}
                        </button>
                        <button
                          className="btn btn-link"
                          onClick={handleEmailRequestOtp}
                          disabled={emailLoading || emailResendCooldown > 0}
                          type="button"
                        >
                          {emailResendCooldown > 0
                            ? `Resend in ${emailResendCooldown}s`
                            : "Resend code"}
                        </button>
                        <button
                          className="btn btn-link text-muted"
                          onClick={() => {
                            setEmailStep("form");
                            setEmailOtpCode("");
                          }}
                          type="button"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <hr />

                {/* Phone Change Section */}
                <div className="row" style={{ padding: 15 }}>
                  <label className="mb-3 h5" style={{ color: "green" }}>
                    Phone Number
                  </label>

                  {phoneStep === "form" ? (
                    <>
                      <div className="col-sm-6 col-lg-4 col-md-6 mb-3">
                        <div className="input-group input-group-outline mb-3">
                          <input
                            className="form-control shadow-none"
                            type="tel"
                            value={newPhone}
                            onChange={(e) => setNewPhone(e.target.value)}
                            placeholder="e.g., 08012345678"
                          />
                        </div>
                        <small className="text-muted">
                          A verification code will be sent to your email address.
                        </small>
                      </div>
                      <div className="col-12">
                        <button
                          className="btn btn-success"
                          onClick={handlePhoneRequestOtp}
                          disabled={phoneLoading || !newPhone}
                          type="button"
                        >
                          {phoneLoading ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            "Send Verification Code"
                          )}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-muted">
                        A verification code has been sent to your email. New phone: <strong>{newPhone}</strong>
                      </p>
                      <div className="col-sm-6 col-lg-4 col-md-6 mb-3">
                        <label className="form-label text-dark">
                          Enter 6-digit code
                        </label>
                        <div className="input-group input-group-outline mb-3">
                          <input
                            className="form-control shadow-none text-center"
                            type="text"
                            maxLength="6"
                            value={phoneOtpCode}
                            onChange={(e) =>
                              setPhoneOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                            }
                            style={{ fontSize: 18, letterSpacing: 8 }}
                          />
                        </div>
                      </div>
                      <div className="col-12">
                        <button
                          className="btn btn-success me-2"
                          onClick={handlePhoneConfirmUpdate}
                          disabled={phoneLoading || phoneOtpCode.length !== 6}
                          type="button"
                        >
                          {phoneLoading ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            "Verify & Update"
                          )}
                        </button>
                        <button
                          className="btn btn-link"
                          onClick={handlePhoneRequestOtp}
                          disabled={phoneLoading || phoneResendCooldown > 0}
                          type="button"
                        >
                          {phoneResendCooldown > 0
                            ? `Resend in ${phoneResendCooldown}s`
                            : "Resend code"}
                        </button>
                        <button
                          className="btn btn-link text-muted"
                          onClick={() => {
                            setPhoneStep("form");
                            setPhoneOtpCode("");
                          }}
                          type="button"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                </div>

                <hr />

                {/* Password Change Section */}
                <div className="row" style={{ padding: 15 }}>
                  <label className="mb-3 h5" style={{ color: "green" }}>
                    Change Password
                  </label>

                  <div className="col-sm-6 col-lg-4 col-md-6 mb-3">
                    <label className="form-label text-dark">
                      Current Password <span className="text-danger">*</span>
                    </label>
                    <div className="input-group input-group-outline mb-3">
                      <input
                        className="form-control shadow-none"
                        type={showPasswords ? "text" : "password"}
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="col-12"></div>

                  <div className="col-sm-6 col-lg-4 col-md-6 mb-3">
                    <label className="form-label text-dark">
                      New Password <span className="text-danger">*</span>
                    </label>
                    <div className="input-group input-group-outline">
                      <input
                        className="form-control shadow-none"
                        type={showPasswords ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                      />
                    </div>
                    {newPassword && (
                      <div className="mt-2">
                        <div
                          style={{
                            height: "4px",
                            backgroundColor: "#e9ecef",
                            borderRadius: "2px",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              height: "100%",
                              width: `${passwordStrength.percentage}%`,
                              backgroundColor: passwordStrength.color,
                              transition: "width 0.3s ease, background-color 0.3s ease",
                            }}
                          />
                        </div>
                        <small style={{ color: passwordStrength.color, fontWeight: "500" }}>
                          Password strength: {passwordStrength.label}
                        </small>
                      </div>
                    )}
                    <small className="text-muted d-block mt-1">
                      Must contain: 6+ characters, 1 number, 1 uppercase, 1 lowercase, 1 special character
                    </small>
                  </div>

                  <div className="col-sm-6 col-lg-4 col-md-6 mb-3">
                    <label className="form-label text-dark">
                      Confirm New Password <span className="text-danger">*</span>
                    </label>
                    <div className="input-group input-group-outline mb-3">
                      <input
                        className="form-control shadow-none"
                        type={showPasswords ? "text" : "password"}
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                      />
                    </div>
                    {confirmNewPassword && newPassword !== confirmNewPassword && (
                      <small className="text-danger">Passwords do not match</small>
                    )}
                  </div>

                  <div className="col-12 mb-3">
                    <input
                      type="checkbox"
                      checked={showPasswords}
                      onChange={(e) => setShowPasswords(e.target.checked)}
                      id="showPasswords"
                    />
                    <label htmlFor="showPasswords" className="ms-2">
                      Show passwords
                    </label>
                  </div>

                  <div className="col-12">
                    <button
                      className="btn btn-success"
                      onClick={handlePasswordChange}
                      disabled={
                        passwordLoading ||
                        !currentPassword ||
                        !newPassword ||
                        !confirmNewPassword
                      }
                      type="button"
                    >
                      {passwordLoading ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        "Change Password"
                      )}
                    </button>
                  </div>
                </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
